import React from 'react';
import {
  CellContext,
  ColumnDef,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Badge, Box, SimpleGrid, Tooltip } from '@chakra-ui/react';
import moment from 'moment/moment';
import 'moment/locale/is';
import { useNavigate } from 'react-router-dom';
import { IconAirplane, IconReader } from 'components/Icons';
import { STATUS_MAPPER } from 'utils/statusUtils';
import { TravelRequestResult } from 'api/types/types';
import StageIndicator from 'components/StageIndicator/StageIndicator';
import StageIndicatorItem from 'components/StageIndicator/StageIndicatorItem';

type ActionsCellProps = {
  id: number;
};
const ActionsCell: React.FC<ActionsCellProps> = ({ id }) => {
  const navigate = useNavigate();
  return (
    <Tooltip label="Skoða beiðni">
      <IconReader
        width={20}
        cursor="pointer"
        onClick={(): void => navigate(`adgerdarsaga?id=${id}`)}
      />
    </Tooltip>
  );
};

export const columns: ColumnDef<TravelRequestResult, any>[] = [
  {
    accessorKey: 'id',
    enableSorting: true,
    header: () => <span>Beiðni</span>,
    accessorFn: (row) => row,
    sortingFn: (rowA: any, rowB: any): any => {
      const idA = Number(rowA.getValue('id').id?.substring(4));
      const idB = Number(rowB.getValue('id').id?.substring(4));
      return idA > idB ? 1 : idA < idB ? -1 : 0;
    },
    sortUndefined: 1,
    cell: ({ getValue }): JSX.Element => {
      const {
        id,
        erpId,
        country,
        city,
        departureDate,
        arrivalDate,
        departureTime,
        arrivalTime,
        createdByName,
        employeeName,
        status,
        lastChanged,
        updatedBy,
      } = getValue() as TravelRequestResult;
      const { shortName } = status;
      return (
        <SimpleGrid columns={{ base: 1, md: 2 }}>
          {lastChanged && (
            <div
              style={{
                position: 'absolute',
                width: '3px',
                background: 'orange',
                top: '0',
                left: '-3px',
                bottom: '0',
              }}
            ></div>
          )}
          <div style={{ display: 'flex' }}>
            <div>
              <span
                style={{
                  color: 'gray',
                  fontSize: '90%',
                }}
              >
                {(erpId && (
                  <Tooltip label={`Kostnaðarskýrsla ferðabeiðnar: ${erpId}`}>
                    {id}
                  </Tooltip>
                )) ||
                  id}
              </span>
              <span
                style={{ color: 'gray', fontSize: '90%', margin: '0 10px' }}
              >
                |
              </span>
            </div>
            <div style={{ marginRight: '10px' }}>
              <Tooltip
                label={
                  <>
                    <span>Beiðni gerð af: {createdByName}</span>
                    {lastChanged && (
                      <>
                        <br />
                        <span>Beiðni breytt af: {updatedBy}</span>
                        <br />
                        <span>
                          Beiðni breytt:{' '}
                          {moment(lastChanged).format('DD. MMMM')}
                        </span>
                      </>
                    )}
                  </>
                }
              >
                {employeeName}
              </Tooltip>

              <Box mt={1}>
                <Tooltip
                  label={
                    <span>
                      <strong>Brottför:</strong>{' '}
                      {moment(departureDate).format('DD. MMMM')}
                      {', '}
                      {departureTime.toLowerCase()} <br />
                      <strong>Koma:</strong>{' '}
                      {moment(arrivalDate).format('DD. MMMM')}
                      {', '}
                      {arrivalTime.toLowerCase()}
                    </span>
                  }
                >
                  <span style={{ color: 'gray', fontSize: '90%' }}>
                    {shortName !== 'DRAFT'
                      ? `${country}, ${city}`
                      : 'Drög að ferðabeiðni'}
                  </span>
                </Tooltip>
              </Box>
            </div>
          </div>
        </SimpleGrid>
      );
    },
  },
  {
    accessorKey: 'agentActions',
    // enableSorting: true,
    // sortDescFirst: true,

    header: () => <span>Vinnsla</span>,
    cell: (props: CellContext<TravelRequestResult, any>): JSX.Element => {
      const agentActions = props.getValue();
      const keyMap = {
        flight: 'Flugbókun',
        hotel: 'Hótelbókun',
        // rentalCar: 'Bílaleigubíll',
      };
      // console.log(props);

      return (
        <div style={{ width: '100%', marginRight: '15px' }}>
          <StageIndicator>
            {agentActions &&
              Object.keys(agentActions).map((key, i) => (
                <StageIndicatorItem
                  key={`${key}_${i}`}
                  label={keyMap[key]}
                  stage={agentActions[key] ? 'done' : ''}
                />
              ))}
          </StageIndicator>
        </div>
      );
    },
  },
  {
    accessorKey: 'departureDate',
    enableSorting: true,
    sortDescFirst: true,
    accessorFn: (row) => row,
    sortingFn: (rowA: any, rowB: any): any => {
      const idA = rowA.getValue('departureDate').departureDate;
      const idB = rowB.getValue('departureDate').departureDate;
      return idA > idB ? 1 : idA < idB ? -1 : 0;
    },
    header: () => <span>Brottför</span>,
    cell: ({ getValue }): string => {
      const { departureDate } = getValue() as TravelRequestResult;
      return moment(departureDate).format('DD. MMM. YYYY');
    },
  },
  {
    accessorKey: 'arrivalDate',
    enableSorting: true,
    header: () => <span>Koma</span>,
    cell: (props: CellContext<TravelRequestResult, any>): string => {
      const arrivalDate = props.getValue();
      return moment(arrivalDate).format('DD. MMM. YYYY');
    },
  },
  {
    accessorKey: 'arrangeFlight',
    enableSorting: true,
    header: () => <span style={{ width: '40px' }}>Flugbókun</span>,
    cell: ({ getValue }): JSX.Element => {
      const arrangeFlight = getValue() as TravelRequestResult;

      return (
        <Tooltip
          label={
            arrangeFlight
              ? 'Starfsmaður vill láta bóka flug'
              : 'Starfsmaður bókar sjálfur flug fyrir sig'
          }
        >
          <Badge colorScheme={arrangeFlight ? 'green' : 'red'} cursor="pointer">
            {arrangeFlight ? 'Já, bóka flug' : 'Ekki bóka flug'}
          </Badge>
        </Tooltip>
      );
    },
  },
  {
    accessorKey: 'arrangeAccommodation',
    enableSorting: true,
    header: () => <span>Hótelbókun</span>,
    cell: ({ getValue }): JSX.Element => {
      const arrangeAccommodation = getValue() as TravelRequestResult;

      return (
        <Tooltip
          label={
            arrangeAccommodation
              ? 'Starfsmaður vill láta bóka hótel'
              : 'Starfsmaður bókar sjálfur hótel fyrir sig'
          }
        >
          <Badge
            colorScheme={arrangeAccommodation ? 'green' : 'red'}
            cursor="pointer"
          >
            {arrangeAccommodation ? 'Já, bóka hótel' : 'Ekki bóka hótel'}
          </Badge>
        </Tooltip>
      );
    },
  },
  {
    accessorKey: 'status',
    enableSorting: true,
    header: () => <span>Staða</span>,
    cell: (props: CellContext<TravelRequestResult, any>): JSX.Element => {
      const { description, shortName } = props.getValue();
      return (
        <Tooltip label={description}>
          <Badge colorScheme={STATUS_MAPPER[shortName]?.color} cursor="pointer">
            {STATUS_MAPPER[shortName]?.label}
          </Badge>
        </Tooltip>
      );
    },
  },

  {
    accessorKey: 'actions',
    header: () => (
      <Box display="flex" marginLeft="auto">
        Aðgerðir
      </Box>
    ),
    accessorFn: (row) => row,
    cell: ({ getValue }): JSX.Element | undefined => {
      const { id } = getValue();
      return (
        <Box float="right">
          <ActionsCell id={id} />
        </Box>
      );
    },
  },
];
